/**
 * Authentication Page Component
 * 
 * Login and signup page for MoneyMojo.
 * 
 * Features:
 * - Toggle between login and signup modes
 * - Form validation and error handling
 * - Responsive design
 * - WCAG 2.2 AA compliance
 * - Dark mode support
 * 
 * @see .specify/memory/constitution.md
 * @see /Reference/Reference design-review/design-principles.md
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User, ArrowRight, Clock, RotateCcw, Loader2 } from 'lucide-react';
import { supabaseAuthService } from '../services/supabase-service';
import { accountRecoveryService } from '../services/account-recovery-service';
import { supabase } from '../lib/supabase';

interface AuthFormData {
  email: string;
  password: string;
  confirmPassword?: string;
  firstName?: string;
  lastName?: string;
  acceptTerms?: boolean;
}

interface AuthPageProps {
  mode?: 'login' | 'signup';
}

export function AuthPage({ mode: initialMode = 'login' }: AuthPageProps): React.ReactElement {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<'login' | 'signup'>(initialMode);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showResendEmail, setShowResendEmail] = useState(false);
  const [resendEmailLoading, setResendEmailLoading] = useState(false);
  const [resendEmailSuccess, setResendEmailSuccess] = useState(false);
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const [showAccountRecovery, setShowAccountRecovery] = useState(false);
  const [accountRecoveryLoading, setAccountRecoveryLoading] = useState(false);
  const [accountRecoveryError, setAccountRecoveryError] = useState<string>('');

  // Check for session expired query parameter
  useEffect(() => {
    if (searchParams.get('sessionExpired') === 'true') {
      setShowSessionExpired(true);
      // Remove query parameter from URL
      navigate(window.location.pathname, { replace: true });
    }
  }, [searchParams, navigate]);
  
  const [formData, setFormData] = useState<AuthFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    acceptTerms: false,
  });

  const handleInputChange = (field: keyof AuthFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    // Signup specific validations
    if (mode === 'signup') {
      if (!formData.firstName) {
        newErrors.firstName = 'First name is required';
      }
      if (!formData.lastName) {
        newErrors.lastName = 'Last name is required';
      }
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
      if (!formData.acceptTerms) {
        newErrors.acceptTerms = 'You must accept the terms and conditions';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});
    
    try {
      if (mode === 'login') {
        // Check if account exists and is soft-deleted before attempting login
        try {
          // First, try to get user by email to check account status
          const { data: { user } } = await supabase.auth.getUser();
          
          // If we can get user info, check account deletion status
          if (user) {
            const statusResult = await accountRecoveryService.checkAccountStatus(user.id);
            if (statusResult.data?.isDeleted && statusResult.data?.canRecover) {
              // Account is soft-deleted but recoverable
              setShowAccountRecovery(true);
              setErrors({ 
                general: 'Your account has been deleted but can still be recovered within 30 days. Click "Recover Account" to restore it.' 
              });
              return;
            }
          }
        } catch (statusError) {
          // If status check fails, continue with normal login attempt
          console.warn('Could not check account status:', statusError);
        }

        // Sign in existing user
        await supabaseAuthService.signIn(formData.email, formData.password);
        
        // After successful login, check if account is soft-deleted
        const { data: { user: loggedInUser } } = await supabase.auth.getUser();
        if (loggedInUser) {
          const statusResult = await accountRecoveryService.checkAccountStatus(loggedInUser.id);
          if (statusResult.data?.isDeleted && statusResult.data?.canRecover) {
            // Account is still marked as deleted - show recovery option
            setShowAccountRecovery(true);
            setErrors({ 
              general: 'Your account has been deleted but can still be recovered. Click "Recover Account" to restore it.' 
            });
            return;
          }
        }
        
        // Always navigate to home page after successful login
        navigate('/dashboard', { replace: true });
      } else {
        // Sign up new user
        const authData = await supabaseAuthService.signUp(formData.email, formData.password);
        
        // If user needs email confirmation, show message
        if (authData?.user && !authData.user.email_confirmed_at) {
          setErrors({ 
            general: 'Account created! Please check your email to confirm your account before signing in.' 
          });
          // Switch to login mode after successful signup
          setTimeout(() => {
            setMode('login');
            setErrors({});
          }, 3000);
        } else if (authData?.session) {
          // If auto-confirmed and session exists, navigate to home page
          navigate('/dashboard', { replace: true });
        } else {
          // Account created but needs confirmation
          setErrors({ 
            general: 'Account created! Please check your email to confirm your account before signing in.' 
          });
          setTimeout(() => {
            setMode('login');
            setErrors({});
          }, 3000);
        }
      }
    } catch (error: any) {
      // Error message is safe to show to user, but don't log sensitive details
      const errorMessage = error?.message || 'Authentication failed. Please try again.';
      
      // Check if error is due to unconfirmed email
      if (errorMessage.includes('Email not confirmed') || errorMessage.includes('email not confirmed')) {
        setErrors({ general: errorMessage });
        setShowResendEmail(true);
      } else {
        setErrors({ general: errorMessage });
        setShowResendEmail(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRecoverAccount = async () => {
    setAccountRecoveryLoading(true);
    setAccountRecoveryError('');
    setErrors({});

    try {
      // Get current user ID (should be available after login attempt)
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setAccountRecoveryError('Please sign in first to recover your account');
        return;
      }

      const result = await accountRecoveryService.recoverAccount(user.id);
      if (result.error) {
        setAccountRecoveryError(result.error.message || 'Failed to recover account');
        return;
      }

      // Account recovered successfully - refresh page or navigate
      setAccountRecoveryError('');
      setShowAccountRecovery(false);
      setErrors({ 
        general: 'Account recovered successfully! You can now use your account normally.' 
      });
      
      // Navigate to home page after a short delay
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 2000);
    } catch (error: any) {
      setAccountRecoveryError(error.message || 'Failed to recover account');
    } finally {
      setAccountRecoveryLoading(false);
    }
  };

  const handleResendConfirmationEmail = async () => {
    if (!formData.email) {
      setErrors({ general: 'Please enter your email address first' });
      return;
    }

    setResendEmailLoading(true);
    setResendEmailSuccess(false);
    setErrors({});

    try {
      await supabaseAuthService.resendConfirmationEmail(formData.email);
      setResendEmailSuccess(true);
      setShowResendEmail(false);
      setErrors({ 
        general: 'Confirmation email sent! Please check your inbox (and spam folder) and click the confirmation link.' 
      });
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to resend confirmation email. Please try again.';
      // Format multi-line error messages
      const formattedMessage = errorMessage.replace(/\n/g, '\n');
      setErrors({ general: formattedMessage });
      // Keep the resend button visible if it's a recoverable error
      if (!errorMessage.includes('already confirmed') && !errorMessage.includes('not registered')) {
        setShowResendEmail(true);
      }
    } finally {
      setResendEmailLoading(false);
    }
  };

  const toggleMode = () => {
    setMode(prev => prev === 'login' ? 'signup' : 'login');
    setErrors({});
    setShowResendEmail(false);
    setResendEmailSuccess(false);
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      acceptTerms: false,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-100 dark:bg-primary-900/20 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-70 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-secondary-100 dark:bg-secondary-900/20 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-70 animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo and Brand */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-4 mb-6">
            <img 
              src="/logo.png"
              srcSet="/logo.png 1x, /Logo-L.png 2x, /Logo-XL.png 3x"
              alt="Money Mojo Logo" 
              className="h-12 w-12 object-contain"
              style={{ 
                imageRendering: 'auto',
                backfaceVisibility: 'hidden',
                transform: 'translateZ(0)'
              }}
              loading="eager"
              width="48"
              height="48"
              onError={(e) => {
                // Fallback if logo fails to load
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 font-brand tracking-tight">
              Money Mojo
            </h1>
          </div>
          <p className="text-lg text-gray-600 dark:text-gray-400 font-medium">
            {mode === 'login' ? 'Welcome back!' : 'Create your account'}
          </p>
        </div>

        {/* Auth Card */}
        <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 p-10 ring-1 ring-gray-100 dark:ring-gray-800">
          {/* Session Expired Message */}
          {showSessionExpired && (
            <div className="mb-6 p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-1">
                    Session Expired
                  </p>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    Your session has expired due to inactivity. Please sign in again.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Mode Toggle */}
          <div className="flex bg-gray-100 dark:bg-gray-700 rounded-xl p-1.5 mb-8">
            <button
              type="button"
              onClick={() => setMode('login')}
              className={`flex-1 py-3 px-6 rounded-lg text-sm font-semibold transition-all duration-300 ${
                mode === 'login'
                  ? 'bg-white dark:bg-gray-600 text-primary-600 dark:text-primary-400 shadow-lg ring-1 ring-primary-100 dark:ring-primary-800'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'
              }`}
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => setMode('signup')}
              className={`flex-1 py-3 px-6 rounded-lg text-sm font-semibold transition-all duration-300 ${
                mode === 'signup'
                  ? 'bg-white dark:bg-gray-600 text-primary-600 dark:text-primary-400 shadow-lg ring-1 ring-primary-100 dark:ring-primary-800'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'
              }`}
            >
              Sign Up
            </button>
          </div>

          {/* General Error */}
          {errors.general && (
            <div className={`mb-6 p-4 rounded-lg border ${
              resendEmailSuccess 
                ? 'bg-success-50 dark:bg-success-900/20 border-success-200 dark:border-success-800' 
                : 'bg-error-50 dark:bg-error-900/20 border-error-200 dark:border-error-800'
            }`}>
              <p className={`text-sm ${
                resendEmailSuccess 
                  ? 'text-success-600 dark:text-success-400' 
                  : 'text-error-600 dark:text-error-400'
              }`}>
                {errors.general}
              </p>
              {showResendEmail && !resendEmailSuccess && (
                <button
                  type="button"
                  onClick={handleResendConfirmationEmail}
                  disabled={resendEmailLoading}
                  className="mt-3 text-sm text-primary-600 dark:text-primary-400 hover:underline font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resendEmailLoading ? 'Sending...' : 'Resend confirmation email'}
                </button>
              )}
              {showAccountRecovery && (
                <div className="mt-4 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-start gap-3">
                    <RotateCcw className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                        Account Recovery Available
                      </p>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
                        Your account was deleted but can still be recovered within 30 days. Recovering your account will restore all your data and settings.
                      </p>
                      {accountRecoveryError && (
                        <p className="text-sm text-red-600 dark:text-red-400 mb-3">
                          {accountRecoveryError}
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={handleRecoverAccount}
                        disabled={accountRecoveryLoading}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {accountRecoveryLoading ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Recovering...</span>
                          </>
                        ) : (
                          <>
                            <RotateCcw className="h-4 w-4" />
                            <span>Recover Account</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-7">
            {/* Signup Fields */}
            {mode === 'signup' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    First Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className={`input pl-12 h-12 text-base ${errors.firstName ? 'border-error-500 ring-error-200' : 'focus:ring-primary-200'}`}
                      placeholder="John"
                    />
                  </div>
                  {errors.firstName && (
                    <p className="text-sm text-error-600 dark:text-error-400 mt-1">{errors.firstName}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    Last Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      className={`input pl-12 h-12 text-base ${errors.lastName ? 'border-error-500 ring-error-200' : 'focus:ring-primary-200'}`}
                      placeholder="Doe"
                    />
                  </div>
                  {errors.lastName && (
                    <p className="text-sm text-error-600 dark:text-error-400 mt-1">{errors.lastName}</p>
                  )}
                </div>
              </div>
            )}

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`input pl-12 h-12 text-base ${errors.email ? 'border-error-500 ring-error-200' : 'focus:ring-primary-200'}`}
                  placeholder="john@example.com"
                  autoComplete="email"
                />
              </div>
              {errors.email && (
                <p className="text-sm text-error-600 dark:text-error-400 mt-1">{errors.email}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className={`input pl-12 pr-12 h-12 text-base ${errors.password ? 'border-error-500 ring-error-200' : 'focus:ring-primary-200'}`}
                  placeholder="Enter your password"
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-sm text-error-600 dark:text-error-400 mt-1">{errors.password}</p>
              )}
            </div>

            {/* Confirm Password (Signup only) */}
            {mode === 'signup' && (
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    className={`input pl-12 pr-12 h-12 text-base ${errors.confirmPassword ? 'border-error-500 ring-error-200' : 'focus:ring-primary-200'}`}
                    placeholder="Confirm your password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-sm text-error-600 dark:text-error-400 mt-1">{errors.confirmPassword}</p>
                )}
              </div>
            )}

            {/* Terms and Conditions (Signup only) */}
            {mode === 'signup' && (
              <div>
                <div className="flex items-start space-x-3">
                  <div className="flex items-center h-5">
                    <input
                      type="checkbox"
                      id="acceptTerms"
                      checked={formData.acceptTerms}
                      onChange={(e) => handleInputChange('acceptTerms', e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="text-sm">
                    <label htmlFor="acceptTerms" className="text-gray-700 dark:text-gray-300">
                      I agree to the{' '}
                      <a 
                        href="/terms" 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        Terms of Service
                      </a>{' '}
                      and{' '}
                      <a 
                        href="/privacy-policy" 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        Privacy Policy
                      </a>
                    </label>
                  </div>
                </div>
                {errors.acceptTerms && (
                  <p className="text-sm text-error-600 dark:text-error-400 mt-1">{errors.acceptTerms}</p>
                )}
              </div>
            )}

            {/* Forgot Password (Login only) */}
            {mode === 'login' && (
              <div className="text-right">
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Forgot your password?
                </Link>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-semibold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98]"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  <span>{mode === 'login' ? 'Signing In...' : 'Creating Account...'}</span>
                </>
              ) : (
                <>
                  <span>{mode === 'login' ? 'Sign In' : 'Create Account'}</span>
                  <ArrowRight className="h-5 w-5" />
                </>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-600" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                  Or continue with
                </span>
              </div>
            </div>
          </div>

          {/* Social Login */}
          <div className="mt-8 grid grid-cols-2 gap-4">
            <button
              type="button"
              className="w-full inline-flex justify-center items-center py-3 px-4 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm bg-white dark:bg-gray-700 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-200 hover:shadow-md transform hover:scale-[1.02]"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="ml-3">Google</span>
            </button>
            <button
              type="button"
              className="w-full inline-flex justify-center items-center py-3 px-4 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm bg-white dark:bg-gray-700 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-200 hover:shadow-md transform hover:scale-[1.02]"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              <span className="ml-3">Facebook</span>
            </button>
          </div>

          {/* Mode Switch */}
          <div className="mt-8 text-center">
            <p className="text-base text-gray-600 dark:text-gray-400">
              {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}{' '}
              <button
                type="button"
                onClick={toggleMode}
                className="text-primary-600 dark:text-primary-400 hover:underline font-semibold transition-colors duration-200"
              >
                {mode === 'login' ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-10 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            By continuing, you agree to our{' '}
            <a 
              href="/terms" 
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline text-primary-600 dark:text-primary-400 font-medium"
            >
              Terms of Service
            </a>
            {' '}and{' '}
            <a 
              href="/privacy-policy" 
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline text-primary-600 dark:text-primary-400 font-medium"
            >
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default AuthPage;
